import React from 'react';

// Add utility function to transform URLs
const transformPublicAssetUrls = (text: string): string => {
  const publicAssetsBaseUrl =
    'https://my-code-dragon.s3.eu-north-1.amazonaws.com/public-assets/';
  return text.replace(
    new RegExp(publicAssetsBaseUrl + '([^\\s\'")]+)', 'g'),
    '$1',
  );
};

interface OutputAreaProps {
  activeTab: string;
  output?: string[] | null;
  isError?: boolean;
  explanationContent?: string;
  tipContent?: string;
  htmlOutput?: string;
  isHtmlOutput?: boolean;
  language?: string;
  debugAiResponse?: string | null;
  explainHighlightedAiResponse?: string | null;
  debugAiLoading?: boolean;
  explainHighlightedAiLoading?: boolean;
  debugError?: string | null;
  explainHighlightedError?: string | null;
  resultSet?: {
    columns: string[];
    rows: any[][];
  };
  resultSets?: {
    columns: string[];
    rows: any[][];
  }[];
  isWaitingForInput?: boolean;
  inputPrompt?: string;
  currentInput?: string;
  onInputChange?: (value: string) => void;
  onInputSubmit?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  terminalOutput?: string[];
}

const OutputArea: React.FC<OutputAreaProps> = ({
  activeTab,
  output,
  isError,
  explanationContent,
  tipContent,
  htmlOutput,
  isHtmlOutput,
  language,
  debugAiResponse,
  explainHighlightedAiResponse,
  debugAiLoading,
  explainHighlightedAiLoading,
  debugError,
  explainHighlightedError,
  resultSet,
  resultSets,
  isWaitingForInput,
  inputPrompt,
  currentInput,
  onInputChange,
  onInputSubmit,
  terminalOutput,
}) => {
  // Function to safely render HTML content
  const renderHtmlPreview = () => {
    if (!htmlOutput)
      return <div className="text-gray-500">No HTML content to display</div>;

    // Check if this is a matplotlib plot
    const isMatplotlibPlot = htmlOutput.includes('matplotlib-output');

    // Check if this is a plotly plot
    const isPlotlyPlot = htmlOutput.includes('plotly-output');

    if (isMatplotlibPlot) {
      // Directly render the matplotlib output with appropriate sandbox settings
      return (
        <div
          className="bg-white p-4 border border-gray-300 rounded"
          dangerouslySetInnerHTML={{ __html: htmlOutput }}
        />
      );
    }

    if (isPlotlyPlot) {
      // Use iframe for Plotly to allow JavaScript execution
      return (
        <div className="bg-white p-4 border border-gray-300 rounded">
          <iframe
            title="Plotly Plot"
            srcDoc={htmlOutput}
            sandbox="allow-scripts allow-same-origin"
            className="w-full min-h-[600px] border-0"
            style={{ height: '600px' }}
          />
        </div>
      );
    }

    return (
      <div className="bg-white p-4 border border-gray-300 rounded">
        <iframe
          title="HTML Preview"
          srcDoc={htmlOutput}
          sandbox="allow-same-origin"
          className="w-full min-h-[300px] border-0"
        />
      </div>
    );
  };

  // Function to render SQL results table
  const renderSQLResults = () => {
    // Handle multiple result sets
    if (resultSets && resultSets.length > 0) {
      return (
        <div className="space-y-6">
          {resultSets.map((resultSet, index) => (
            <div key={index}>
              {resultSets.length > 1 && (
                <h4 className="text-lg font-semibold mb-3 text-gray-700">
                  Query {index + 1} Results:
                </h4>
              )}
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-300">
                  <thead>
                    <tr className="bg-gray-100">
                      {resultSet.columns.map((column, colIndex) => (
                        <th
                          key={colIndex}
                          className="py-2 px-4 text-left border-b"
                        >
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {resultSet.rows.map((row, rowIndex) => (
                      <tr key={rowIndex} className="hover:bg-gray-50">
                        {row.map((cell, cellIndex) => (
                          <td key={cellIndex} className="py-2 px-4 border-b">
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      );
    }

    // Handle single result set (backward compatibility)
    if (!resultSet) return null;

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-300">
          <thead>
            <tr className="bg-gray-100">
              {resultSet.columns.map((column, index) => (
                <th key={index} className="py-2 px-4 text-left border-b">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {resultSet.rows.map((row, rowIndex) => (
              <tr key={rowIndex} className="hover:bg-gray-50">
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex} className="py-2 px-4 border-b">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // For typing editor, tab 0 is Debug and tab 1 is Tips
  const isTypingEditor = language === 'typing';

  // Custom CSS for lists in explanation and tip content
  const customListStyles = `
    ol {
      list-style-type: decimal;
      margin-left: 1.5rem;
      margin-bottom: 1rem;
    }
    ul {
      list-style-type: disc;
      margin-left: 3rem;
      margin-bottom: 1rem;
    }
    li {
      margin-bottom: 0.5rem;
    }
  `;

  // Function to clean HTML content by removing unnecessary spaces and line breaks
  const cleanHtmlContent = (html?: string): string => {
    if (!html) return '';
    // Replace multiple line breaks with a single one
    return html
      .replace(/(\r\n|\n|\r){2,}/g, '\n')
      .replace(/>\s+</g, '><') // Remove whitespace between tags
      .trim();
  };

  // Function to render terminal with input
  const renderTerminal = () => {
    return (
      <div className="font-mono text-sm">
        {/* Show terminal history */}
        {terminalOutput
          ?.filter((line) => line !== inputPrompt)
          ?.map((line, index) => (
            <div key={index} className="whitespace-pre-wrap">
              {transformPublicAssetUrls(line)}
            </div>
          ))}

        {/* Show input prompt if waiting for input */}
        {isWaitingForInput && (
          <div className="flex items-center">
            <span>{transformPublicAssetUrls(inputPrompt || '')}</span>
            <input
              type="text"
              value={currentInput}
              onChange={(e) => onInputChange?.(e.target.value)}
              onKeyDown={onInputSubmit}
              className="flex-1 ml-1 bg-transparent border-none outline-none"
              autoFocus
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="border border-gray-300 bg-white p-4 rounded-b-lg min-h-[200px]">
      {/* Explanation tab */}
      {activeTab === '0' && !isTypingEditor && (
        <div className="bg-blue-50 p-4 rounded-md border border-blue-200">
          <p className="font-semibold text-lg mb-3">Explanation of the Code</p>
          <p className="text-gray-700 whitespace-pre-wrap">
            <div
              dangerouslySetInnerHTML={{
                __html:
                  cleanHtmlContent(explanationContent) ||
                  'Code explanation not available.',
              }}
              className="output-area space-y-4 text-sm md:text-base explanation-content"
            />
          </p>
        </div>
      )}

      {/* For typing editor: output in tab 0 */}
      {activeTab === '0' && isTypingEditor && (
        <div
          className={`p-4 rounded-md ${output && output.length > 0 ? 'bg-blue-50 border-blue-200' : isError ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}
        >
          <p className="font-semibold text-lg mb-3">Output</p>
          {debugAiLoading ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
              <span className="ml-2 text-blue-600">Getting AI feedback...</span>
            </div>
          ) : debugAiResponse ? (
            <div>
              {output && output.length > 0 && (
                <pre
                  className={`whitespace-pre-wrap font-mono text-sm ${isError ? 'text-red-700' : 'text-green-800'}`}
                >
                  {output
                    .map((line) => transformPublicAssetUrls(line))
                    .join('\n')}
                </pre>
              )}
            </div>
          ) : output && output.length > 0 ? (
            <pre
              className={`whitespace-pre-wrap font-mono text-sm ${isError ? 'text-red-700' : 'text-green-800'}`}
            >
              {output.map((line) => transformPublicAssetUrls(line)).join('\n')}
            </pre>
          ) : (
            <p className="text-gray-700">
              Type the text and click "Submit and Check" to see feedback here.
            </p>
          )}
        </div>
      )}

      {/* Tips tab */}
      {activeTab === '1' && (
        <div className="bg-blue-50 p-4 rounded-md border border-blue-200">
          <p className="font-semibold text-lg mb-3">My Code Dragon Tip</p>
          <div
            dangerouslySetInnerHTML={{
              __html:
                cleanHtmlContent(tipContent) || 'Coding tips not available.',
            }}
            className="output-area text-gray-700 whitespace-pre-wrap"
          />
        </div>
      )}

      {/* Output tab */}
      {activeTab === '2' && !isTypingEditor && (
        <div
          className={`p-4 border rounded-md ${output && output.length === 0 ? 'bg-blue-50 border-blue-200' : isError ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}
        >
          <p className="font-semibold text-lg mb-3">Output</p>

          {/* HTML Preview when applicable */}
          {isHtmlOutput && htmlOutput ? (
            <div>
              <div className="mb-4">{renderHtmlPreview()}</div>
              {/* If there's also text output, show it below the HTML preview */}
              {output &&
                output.length > 0 &&
                !output[0].includes('Plot generated successfully') && (
                  <pre className="whitespace-pre-wrap font-mono text-sm text-green-800 mt-4">
                    {output
                      .map((line) => transformPublicAssetUrls(line))
                      .join('\n')}
                  </pre>
                )}
            </div>
          ) : /* SQL Results when applicable */
          resultSet || (resultSets && resultSets.length > 0) ? (
            <div>
              <div className="mb-4">{renderSQLResults()}</div>
              {output && output.length > 0 && (
                <pre className="whitespace-pre-wrap font-mono text-sm text-green-800">
                  {output
                    .map((line) => transformPublicAssetUrls(line))
                    .join('\n')}
                </pre>
              )}
            </div>
          ) : /* Terminal output with input handling */
          isWaitingForInput || terminalOutput?.length ? (
            renderTerminal()
          ) : /* Regular code output */
          output && output.length > 0 ? (
            <pre
              className={`whitespace-pre-wrap font-mono text-sm ${isError ? 'text-red-700' : 'text-green-800'}`}
            >
              {output.map((line) => transformPublicAssetUrls(line)).join('\n')}
            </pre>
          ) : (
            <p className="text-gray-700">
              Click "Submit and Check" to see output information here.
            </p>
          )}
        </div>
      )}

      {/* Debug output tab */}
      {activeTab === '3' && (
        <div className="bg-blue-50 p-4 rounded-md border border-blue-200">
          <p className="font-semibold text-lg mb-3">Debug Output</p>

          {debugAiLoading ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
              <span className="ml-2 text-blue-600">
                Getting AI assistance...
              </span>
            </div>
          ) : debugError ? (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-600">
              {debugError}
            </div>
          ) : debugAiResponse ? (
            <p className="whitespace-pre-wrap text-gray-700">
              {debugAiResponse}
            </p>
          ) : (
            <p className="text-gray-700">
              Click "Debug My Code" to get AI assistance with your code.
            </p>
          )}
        </div>
      )}

      {/* Explain Highlighted Code tab */}
      {activeTab === '4' && (
        <div className="bg-blue-50 p-4 rounded-md border border-blue-200">
          <p className="font-semibold text-lg mb-3">
            Highlighted Code Explanation
          </p>

          {explainHighlightedAiLoading ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
              <span className="ml-2 text-blue-600">
                Getting AI explanation...
              </span>
            </div>
          ) : explainHighlightedError ? (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-600">
              {explainHighlightedError}
            </div>
          ) : explainHighlightedAiResponse ? (
            <p className="whitespace-pre-wrap text-gray-700">
              {explainHighlightedAiResponse}
            </p>
          ) : (
            <div>
              <p className="text-gray-700 mb-2">
                Highlight some code in the{' '}
                <strong>left-side provided code editor</strong> and click
                "Explain Highlighted Code" to get an AI explanation.
              </p>
              <div className="p-2 bg-yellow-50 border border-yellow-200 rounded text-sm">
                <p className="font-medium text-yellow-700">
                  How to highlight code:
                </p>
                <ol className="list-decimal ml-5 text-yellow-800">
                  <li>
                    Click and drag your mouse over the code in the{' '}
                    <strong>left editor</strong> (provided code)
                  </li>
                  <li>
                    Make sure some text is selected - you should see it
                    highlighted
                  </li>
                  <li>Click the "Explain Highlighted Code" button</li>
                </ol>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OutputArea;
